import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useAppContext } from '../context/AppContext';
import DatePicker from "react-datepicker";
//import { FaIdCardAlt, FaUserInjured } from 'react-icons/fa';
//import { MdCancel, MdLocationOn } from 'react-icons/md';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { calculateAge } from '../utils/ageCalculator';

import "react-datepicker/dist/react-datepicker.css";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.theme.spacing.large};
  color: ${props => props.theme.colors.text};
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.large};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${props => props.theme.spacing.medium};
`;

const FormGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  align-items: center;
  gap: ${props => props.theme.spacing.large};
`;

const Label = styled.label`
  font-weight: bold;
  text-align: right;
`;

const InputWrapper = styled.div`
  width: 100%;
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.small};
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.secondary};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  width: 96%;
  height: 38px; // Match the height of react-select
`;

const StyledDatePicker = styled(DatePicker)`
  padding: ${props => props.theme.spacing.small};
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.secondary};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  width: 96%;
  height: 38px; // Match the height of react-select
`;

const RadioGroup = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
`;

const ButtonContainer = styled.div`
  grid-column: 2;
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.large};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.medium};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
  width: 67%;  // This will make it full width of its container

  &:hover {
    opacity: 0.8;
  }
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
    borderColor: 'rgb(71, 71, 71)',
    borderRadius: '20px',
    padding: '0.25rem',
    color: 'white',
    width: '100%',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'white',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FDBA31' : 'rgb(71, 71, 71)',
    color: state.isSelected ? 'black' : 'white',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
  }),
};

const useAthleteForm = (initialAthlete) => {
  const [athlete, setAthlete] = useState(initialAthlete);

  const handleAthleteInfoChange = useCallback((value, field) => {
    setAthlete(prev => {
      const updated = { ...prev, [field]: value };

      // Keep derived fields in sync
      if (field === 'firstName' || field === 'lastname') {
        updated.name = `${updated.firstName} ${updated.lastname}`.trim();
      }
      if (field === 'dob') {
        const age = calculateAge(value);
        updated.age = age;

        // Validate age 
        if (age < 3 || age > 120) {
          Swal.fire({
            title: 'Invalid Age',
            text: 'Age must be between 3 and 120 years.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
          // Reset to current date if invalid
          updated.dob = new Date();
          updated.age = calculateAge(updated.dob);
        }
      }

      // When program changes, clear the group
      if (field === 'program') {
        updated.group = '';
      }

      return updated;
    });
  }, []);

  return { athlete, setAthlete, handleAthleteInfoChange };
};

const AthleteAdminPage = () => {
  const { 
    user,
    campusList,
    view,
    setView,
    athleteNamesList,
    currentCampus,
    currentGender,
    currentProgram,
    currentEvalCount,
    athlete: contextAthlete,
    setAthlete: setContextAthlete,
    PROGRAMS,
    PROGRAM_GROUPINGS,
    flask_post,
    updateAthleteList,
    currentDataType
  } = useAppContext();

  const isCreateMode = useMemo(() => view === 'athleteAdminCreate', [view]);

  const initialAthlete = useMemo(() => ({
    name: '', firstName: '', lastname: '', age: '', dob: new Date(),
    gender: '', program: '', group: '', sport: 'NA', campus: '', team: 'NA', injury: 'NA'
  }), []);

  const { athlete, setAthlete, handleAthleteInfoChange } = useAthleteForm(initialAthlete);

  // Determine if the current program has groupings (used for render-time cues)
  const currentProgramGroupingEntry = useMemo(() => PROGRAM_GROUPINGS.find(p => p.program === athlete?.program), [athlete?.program, PROGRAM_GROUPINGS]);
  const currentProgramHasGroups = currentProgramGroupingEntry && currentProgramGroupingEntry.groupings.length > 0;

  useEffect(() => {
    if (!isCreateMode && contextAthlete) {
      // Calculate age when loading existing athlete
      const athleteWithAge = { ...contextAthlete };
      if (athleteWithAge.dob) {
        athleteWithAge.age = calculateAge(new Date(athleteWithAge.dob));
      }
      setAthlete(athleteWithAge);
    } else {
      setAthlete(initialAthlete);
    }
  }, [isCreateMode, contextAthlete, initialAthlete, setAthlete]);

  const handleAthleteSelection = useCallback(async (selected) => {
    const response = await flask_post('LoadAthlete', [selected.value]);
    const athleteData = response.data;
    // Calculate age when loading athlete
    if (athleteData.dob) {
      athleteData.age = calculateAge(new Date(athleteData.dob));
    }
    setContextAthlete(athleteData);
    setAthlete(athleteData);
  }, [flask_post, setContextAthlete, setAthlete]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Determine if the chosen program has defined groupings (e.g. Team ATHlete)
    const programGroupingEntry = PROGRAM_GROUPINGS.find(p => p.program === athlete?.program);
    const programHasGroups = programGroupingEntry && programGroupingEntry.groupings.length > 0;

    // ---------- Field-specific validation and messaging ----------
    if (!athlete?.firstName) {
      Swal.fire('Error', 'First name is required', 'error');
      return;
    }

    if (!athlete?.lastname) {
      Swal.fire('Error', 'Last name is required', 'error');
      return;
    }

    if (isCreateMode && !athlete?.campus) {
      Swal.fire('Error', 'Please select a campus', 'error');
      return;
    }

    if (!athlete?.program) {
      Swal.fire('Error', 'Please select a program', 'error');
      return;
    }

    if (programHasGroups && !athlete?.group) {
      Swal.fire('Error', 'Please select a group for the chosen program', 'error');
      return;
    }

    const gender = athlete.gender?.trim();
    if (!gender) {
      Swal.fire('Error', 'Please select a gender', 'error');
      return;
    }

    const ageIsValid = athlete?.age && Number(athlete.age) >= 3 && Number(athlete.age) <= 120;
    if (!ageIsValid) {
      Swal.fire('Error', 'Date of birth results in an invalid age (should be between 3 and 120). Please adjust DOB.', 'error');
      return;
    }

    try {
      // Build payload: on edit we also send the original name so the back-end can locate the record
      const payload = isCreateMode ? [athlete, user.campus] : [athlete, contextAthlete?.name];
      const response = await flask_post(isCreateMode ? 'CreateAthlete' : 'EditAthlete', payload);

      if (response.data) {
        if (!isCreateMode) {
          setContextAthlete(athlete);
        }

        updateAthleteList(currentCampus, currentProgram, currentGender, currentEvalCount, currentDataType, athlete);

        Swal.fire('Success', `Athlete ${isCreateMode ? 'created' : 'updated'} successfully`, 'success');
        setView('athleteAdminEdit');
      } else {
        // Back-end returned false (athlete not found or update failed)
        Swal.fire('Error', `Failed to ${isCreateMode ? 'create' : 'update'} athlete – athlete not found`, 'error');
      }
    } catch (error) {
      console.error('Athlete submit error:', error);
      Swal.fire('Error', `Failed to ${isCreateMode ? 'create' : 'update'} athlete`, 'error');
    }
  }, [athlete, isCreateMode, user.campus, flask_post, setContextAthlete, updateAthleteList, currentCampus, currentProgram, currentGender, currentEvalCount, currentDataType, setView, PROGRAM_GROUPINGS, contextAthlete]);

  return (
    <PageContainer>
      <Title>{isCreateMode ? 'Create Athlete Profile' : 'Edit Athlete Profile'}</Title>
      <Form onSubmit={handleSubmit}>
        {!isCreateMode && (
          <FormGroup>
            <Label>Athlete:</Label>
            <InputWrapper>
              <Select
                options={athleteNamesList.map(name => ({ value: name, label: name }))}
                value={athlete?.name ? { value: athlete.name, label: athlete.name } : null}
                onChange={handleAthleteSelection}
                styles={customSelectStyles}
                placeholder="Select athlete"
              />
            </InputWrapper>
          </FormGroup>
        )}

        <FormGroup>
          <Label>First Name:</Label>
          <Input
            type="text"
            value={athlete?.firstName || ''}
            onChange={(e) => handleAthleteInfoChange(e.target.value, 'firstName')}
            placeholder="First name"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Last Name:</Label>
          <Input
            type="text"
            value={athlete?.lastname || ''}
            onChange={(e) => handleAthleteInfoChange(e.target.value, 'lastname')}
            placeholder="Last name"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Campus:</Label>
          <InputWrapper>
            <Select
              options={campusList.map(campus => ({ value: campus, label: campus }))}
              value={athlete?.campus ? { value: athlete.campus, label: athlete.campus } : null}
              onChange={(selected) => handleAthleteInfoChange(selected.value, 'campus')}
              styles={customSelectStyles}
              placeholder="Select campus"
            />
          </InputWrapper>
        </FormGroup>

        <FormGroup>            
          <Label>Program:</Label>
          <InputWrapper>
            <Select
              options={PROGRAMS.map(program => ({ value: program, label: program }))}
              value={athlete?.program ? { value: athlete.program, label: athlete.program } : null}
              onChange={(selected) => handleAthleteInfoChange(selected.value, 'program')}
              styles={customSelectStyles}
              placeholder="Select program"
            />
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Group:</Label>
          <InputWrapper>
<Select
              options={(PROGRAM_GROUPINGS.find(p => p.program === athlete?.program)?.groupings || []).map(group => ({ value: group, label: group }))}
              value={athlete?.group ? { value: athlete.group, label: athlete.group } : null}
              onChange={(selected) => handleAthleteInfoChange(selected.value, 'group')}
              styles={customSelectStyles}
              placeholder={athlete?.program ? "Select group" : "Select program first"}
              isDisabled={!athlete?.program}
              // Provide a visual cue for required when applicable
              className={currentProgramHasGroups ? 'required-select' : ''}
            />
          </InputWrapper>
        </FormGroup>

        {athlete?.program === 'Rebuild ATHlete' && (
          <FormGroup>
            <Label>Specify Injury:</Label>
            <Input
              type="text"
              value={athlete?.injury || ''}
              onChange={(e) => handleAthleteInfoChange(e.target.value, 'injury')}
              placeholder="injury, e.g. knee injury"
            />
          </FormGroup>
        )}

        {athlete?.program !== 'Forever ATHlete' && (
          <FormGroup>
            <Label>Sport:</Label>
            <Input
              type="text"
              value={athlete?.sport || ''}
              onChange={(e) => handleAthleteInfoChange(e.target.value, 'sport')}
              placeholder="sport"
            />
          </FormGroup>
        )}

        {athlete?.program === 'Team ATHlete' && (
          <FormGroup>
            <Label>Team Name:</Label>
            <Input
              type="text"
              value={athlete?.team || ''}
              onChange={(e) => handleAthleteInfoChange(e.target.value, 'team')}
              placeholder="team name"
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>DOB:</Label>
          <StyledDatePicker
            selected={athlete?.dob ? new Date(athlete.dob) : new Date()}
            onChange={(date) => handleAthleteInfoChange(date, 'dob')}
            dateFormat="dd/MM/yyyy"
            placeholderText='date of birth'
          />
        </FormGroup>

        <FormGroup>
          <Label>Age:</Label>
          <Input
            type="text"
            value={athlete?.age || ''}
            readOnly
            disabled
            placeholder="age"
          />
        </FormGroup>

        <FormGroup>
          <Label>Gender:</Label>
          <RadioGroup>
            <label>
              <input
                type="radio"
                value="Male"
                name="gender"
                checked={athlete?.gender === 'Male'}
                onChange={(event) => handleAthleteInfoChange(event.target.value, 'gender')}
                required
              />
              Male
            </label>
            <label>
              <input
                type="radio"
                value="Female"
                name="gender"
                checked={athlete?.gender === 'Female'}
                onChange={(event) => handleAthleteInfoChange(event.target.value, 'gender')}
                required   // add here too
              />
              Female
            </label>
          </RadioGroup>
        </FormGroup>

        <ButtonContainer>
          <Button type="submit">
            {isCreateMode ? "Create" : "Update"}
          </Button>
        </ButtonContainer>
      </Form>
    </PageContainer>
  );
};

export default AthleteAdminPage;